import { Eraser } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';

/**
 * חתימה באצבע או בעכבר. מחזיר PNG כ-data URL (או null כשריק).
 *
 * הקנבס מצויר ברזולוציית המסך (חד בטלפון), והייצוא מוקטן לרוחב של עד 900px —
 * כך החתימה נשארת עשרות KB, מתחת לתקרה של השרת.
 */
export function SignaturePad({ onChange, label }: { onChange: (png: string | null) => void; label: string }) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvas.current!;
    const fit = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      c.width = Math.round(rect.width * ratio);
      c.height = Math.round(rect.height * ratio);
      const ctx = c.getContext('2d')!;
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0B2545';
    };
    fit();
  }, []);

  const point = (e: React.PointerEvent) => {
    const rect = canvas.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const exportPng = () => {
    const c = canvas.current!;
    const scale = Math.min(1, 900 / c.width);
    const out = document.createElement('canvas');
    out.width = Math.round(c.width * scale);
    out.height = Math.round(c.height * scale);
    out.getContext('2d')!.drawImage(c, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  };

  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    canvas.current!.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current || !last.current) return;
    const p = point(e);
    const ctx = canvas.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (!empty) onChange(exportPng());
  };
  const clear = () => {
    const c = canvas.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="space-y-1.5">
      <div className="relative rounded-xl border border-dashed border-border-strong bg-white">
        <canvas
          ref={canvas}
          role="img"
          aria-label={label}
          className="h-40 w-full touch-none rounded-xl"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
        />
        {empty ? (
          <span className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-slate-400">
            {t('signature.placeholder')}
          </span>
        ) : null}
        <span className="pointer-events-none absolute inset-x-6 bottom-8 border-b border-slate-300" aria-hidden />
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" disabled={empty} onClick={clear}>
          <Eraser aria-hidden />
          {t('signature.clear')}
        </Button>
      </div>
    </div>
  );
}
